// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { IDevice } from '../constants/device'
import Emitter from 'mitt'

export interface IDeviceState
  extends Pick<
    IDevice,
    'cityID' | 'testMode' | 'taskType' | 'driverID' | 'deviceType' | 'billingCycleStart' | 'environment'
  > {}

export interface IDeviceUpdateModelState {
  device: IDeviceState
  dirty: boolean
}

export class DeviceUpdateModel {
  private device: IDeviceState
  private originDevice: IDeviceState

  private dirty: boolean = false
  public readonly emitter = Emitter()

  constructor (device: IDevice) {
    this.device = {
      cityID: device.cityID,
      driverID: device.driverID,
      testMode: device.testMode,
      taskType: device.taskType,
      deviceType: device.deviceType,
      billingCycleStart: device.billingCycleStart,
      environment: device.environment
    }
    this.originDevice = { ...this.device }
  }

  set (key: string, value: unknown) {
    const oldValue = this.device[key]
    this.device[key] = value
    this.checkEquality()
    let hasChanged = oldValue !== value
    if (hasChanged) {
      this.emitter.emit('change', this.getState())
    }
  }

  getState (): IDeviceUpdateModelState {
    return {
      device: { ...this.device },
      dirty: this.dirty
    }
  }

  reset () {
    if (this.dirty === false) {
      return
    }
    this.device = { ...this.originDevice }
    this.dirty = false
    this.emitter.emit('change', this.getState())
  }

  getDirtyFields (): Partial<IDevice> {
    let values: Partial<IDevice> = {}
    for (let k in this.device) {
      if (this.isFieldDirty(k)) {
        values[k] = this.device[k]
      }
    }
    return values
  }

  isDirty (): boolean {
    return this.dirty
  }

  private isFieldDirty (key) {
    const noDirty = this.device[key] === this.originDevice[key]
    return !noDirty
  }

  private checkEquality () {
    let noDirty = true
    for (let k in this.device) {
      noDirty = !this.isFieldDirty(k)
      if (noDirty === false) {
        break
      }
    }
    this.dirty = !noDirty
  }

  dispose () {
    this.emitter.all.clear()
  }
}
